/**
 * Twin runs resource — typed wrapper over `POST /v1/twin/runs` (the
 * "Run heat" action on the process graph).
 *
 * One call generates ONE complete heat for the active org: a batch + its
 * lots / operations / tracked_units (castings + moulds + specimens + QC).
 * The endpoint is synchronous (~<1s) and returns the provisioned counts so
 * the UI can report them in the success toast and immediately refresh the
 * Batches + Parts surfaces.
 *
 * Foundry-pack-gated + member-role-gated server-side; the SPA just calls it
 * with the active org like every other /v1/twin mutation (the X-Org-Id
 * header is attached by the api client). Errors come back as the standard
 * envelope `{ error: <code> }`.
 */

import { api } from "@/lib/api-client";

/**
 * What a successful run provisions. Returned by the run endpoint (201) so
 * the UI can report exact counts in the success toast.
 */
export interface RunResult {
  /** The generated batch (heat) id. */
  heat_id: string;
  /** Human-readable heat number (e.g. "H-04822"). */
  heat_number: string;
  /** How many material lots the heat created. */
  lots: number;
  /** How many lot operations were recorded. */
  operations: number;
  /** How many castings were tracked. */
  castings: number;
  /**
   * Every tracked unit the heat produced, of any material class — reported
   * SEPARATELY from `castings` because they are different facts. A custom
   * plant's units may not classify as castings at all, and folding the total
   * into `castings` is what made a 122-casting heat announce "247 castings".
   */
  units_total?: number;
  /** How many moulds were tracked. */
  moulds: number;
  /** How many test specimens were tracked. */
  specimens: number;
}

/** What a successful regenerate did (200) — counts so the toast can report them. */
export interface RegenerateResult {
  /** Heats emitted from the current plant. */
  heats: number;
  /** Rows cleared across the org's genealogy tables. */
  cleared: number;
  lots: number;
  operations: number;
  /** Tracked units (parts) emitted across all heats. */
  units: number;
}

export const twinRuns = {
  /**
   * Generate one complete heat for the active org — a batch + its lots,
   * operations and tracked units (castings, moulds, specimens, QC). 201.
   *
   * `recipeId` is OPTIONAL (Phase-2 Wave-0): when a recipe is picked, its id is
   * threaded into the body as `{ recipe_id }` and the backend loads that
   * org-scoped recipe so the SELECTED pattern's per-cavity designed weight drives
   * the pour's casting mass. Omitting it posts `{}` — today's no-recipe behaviour,
   * byte-identical.
   */
  run: (recipeId?: string) =>
    api.post<RunResult>("/v1/twin/runs", recipeId ? { recipe_id: recipeId } : {}),
  /**
   * Clear the org's synthetic heats and re-emit a fresh batch from the CURRENT
   * plant (plant-driven), so Batches / Parts / Correlations reflect the live
   * process after a model change. Destructive. 200.
   */
  regenerate: () => api.post<RegenerateResult>("/v1/twin/runs/regenerate", {}),
};
